import type { User } from '@/configs/apis/user';
import type { CreateGroupParams } from '@/configs/apis/groups/interface';
import type { ChatGroupMemberParams } from '../useChatGroup/types';
import type { useChats } from '.';

export enum ChatCategory {
  SINGLE = 0,
  GROUP = 1,
  TEMP = 2,
  GROUP_MSG_STATUS = 3,
}

export enum ChatSystemChats {
  SINGLE = 'P_SYSTEM_PERSONAL_NOTICE',
}

export const ChatSystemChatsValues = Object.values(ChatSystemChats);

export type UseChatsResult = ReturnType<typeof useChats>;

export interface DBChat {
  id: number;
  category: ChatCategory;
  state: number;
  chat_id: string;
  receiver_id: string;
  name: string;
  avatar: string;
  ord: number;
  tag: string;
  /**
   * @deprecated Compatible with DB fields, temporarily retained
   */
  latest_msg_relate_id_type?: number;
  /**
   * @deprecated Compatible with DB fields, temporarily retained
   */
  latest_msg_relate_id?: string;
  last_read_message_id: number;
  create_at: number;
  update_at: number;
  expire_duration: number;
}

export interface ServerChat {
  chat_id: string;
  avatar: string;
  name: string;
  user_id: string;
}

export interface Chat extends DBChat {
  // State fields
  loading?: boolean;
  user: Omit<User, 'access'>;
  last_read_new_message_id?: number;
}

export type ChatChatId = Chat['chat_id'];

export type ChatPartial = Partial<Pick<Chat, 'user'>> & Partial<Omit<Chat, 'user'>>;

export interface ChatInfo {
  index: number;
  chat: Chat;
}

export type ChatInfoMap = Record<ChatChatId, ChatInfo>;

export type ChatQuery = Pick<DBChat, 'chat_id'>;

export type DBChatParams = ChatQuery & Partial<DBChat>;

export enum InitType {
  CREATE = 'CREATE',
  LOAD = 'LOAD',
}

export type ChatParams = ChatQuery &
  ChatPartial & { users?: ChatGroupMemberParams[]; initType?: keyof typeof InitType };

export type CreateSingleChatParams = {
  userId: string;
} & ChatPartial;

export type CreateGroupChatParams = {
  users?: ChatGroupMemberParams[];
} & ChatPartial &
  CreateGroupParams &
  Partial<Pick<CreateGroupParams, 'group_type' | 'related_gid' | 'group_name'>>;

export type CreateChatParams = CreateSingleChatParams | CreateGroupChatParams;

export type SendChatParams = Pick<Chat, 'category' | 'receiver_id'> & ChatParams;

export type ChatArgType<T> = ChatQuery & {
  messages: T[];
};
